import React from 'react';

import {Group, Item} from '@pb/kit-component-group';

export const EVENT_CODE_INFO = (
    <Group direction="v">
        <Group direction="v" gap="xxs">
            <Item>Short semantic construction that should display the sense of the event.</Item>
            <Item>Use <strong>uppercase</strong> and <strong>underscore</strong>.</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item>Examples:</Item>
            <Item>"VCP_DT"</Item>
            <Item>"INDX_TIME_PRD"</Item>
            <Item>"ADMIN_USER_SUBMIT"</Item>
        </Group>
    </Group>
);

export const EVENT_TYPE_INFO = (
    <Group direction="v">
        <Item>Indicates whether the event belongs to User or System tracking.</Item>
    </Group>
);

export const EVENT_SOURCE_INFO = (
    <Group direction="v">
        <Item>Describes the product associated with the event.</Item>
        <Group direction="v" gap="xxs">
            <Item>Examples:</Item>
            <Item>"PLATFORM"</Item>
            <Item>"MOBILE"</Item>
            <Item>"NEWSLETTER"</Item>
        </Group>
    </Group>
);

export const EVENT_DESCRIPTION_INFO = (
    <Group direction="v">
        <Group direction="v" gap="xxs">
            <Item>Succinct description of the event.</Item>
            <Item>
                Use the pattern <strong>"Feature Name or Place - [Intermediate Element] - User/System Action"</strong>
            </Item>
            <Item>where</Item>
            <Item><strong>Feature Name or Place:</strong></Item>
            <Item>The main feature where the interaction occurs.</Item>
            <Item><i>Examples:</i> Fund Profile, Conversational Search, Advanced Search Page.</Item>
            <Item><strong>Intermediate Element (optional):</strong></Item>
            <Item>Use if it helps clarify the navigation path before the final action.</Item>
            <Item>It can represent subsections, components, or steps within a larger feature.</Item>
            <Item>Avoid using overly generic UI terms like <i>icon</i>, <i>pill</i>, or <i>link</i>.</Item>
            <Item>Limit to a maximum of 3 levels.</Item>
            <Item><i>Examples:</i> Trade Data Tab, Edit Table, Layout Menu.</Item>
            <Item><strong>User/System Action:</strong></Item>
            <Item>A concise action description – typically a verb or verb + noun.</Item>
            <Item><i>Examples:</i> Change Description, Select Time Period, Close, System Response.</Item>
            <Item><strong>Each word should be capitalized.</strong></Item>
            <Item>Existing naming conventions should be reused if the implemented functionality is similar.</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item>Examples:</Item>
            <Item>"Fund Profile – Change Description"</Item>
            <Item>"View Fund Profile – Trade Data Tab – Select Time Period"</Item>
            <Item>"Admin – Search Results – Edit Table – Rename Layout – Cancel"</Item>
            <Item>"Conversational Search - System Response - Right Rail"</Item>
        </Group>
    </Group>
);

export const FAMILIES_INFO = (
    <Group direction="v" gap="xxs">
        Reflects belonging to the business module of the PitchBook product.
        Choose the appropriate one or several in the drop-down.
    </Group>
);

export const TRIGGER_INFO = (
    <span>Describes the user action that triggers the event.
        List all triggers if there is more than one for the event.
    </span>
);

export const DUE_DATE_INFO = (
    <Group direction="v" gap="xxs">
        <Item>The date by which the event should be processed.</Item>
        <Item><strong>7 business days by default</strong>.</Item>
    </Group>
);

export const AUTHOR_INFO = (
    <Group direction="v" gap="xxs">
        <Item>Creator of the change request.</Item>
        <Item><strong>There can only be one author per change request.</strong></Item>
    </Group>
);

export const REVIEWER_INFO = (
    <Group direction="v" gap="xxs">
        <Item>
            <strong>Reviewer:</strong> QA Engineer providing feedback and recommendations on the change request.
        </Item>
        <Item><strong>Approver:</strong> Product Owner providing final approval for the change request.</Item>
    </Group>
);

export const REPORTED_INFO =
    <span>Determines whether the event is included in usage reports available in PB Admin.</span>;


export const MIXPANEL_DECORATION_INFO = (
    <Group direction="v">
        <Item>
            Determines whether additional attributes will be attached to the event when sent to Mixpanel. Decoration
            can be based on the entity type (Company, Investor, etc) or Research Report. Attributes required to enable
            the decoration will be automatically added to the list of attributes associated with the event.
        </Item>
        <Item>
            <strong>Note:</strong> Runtime decoration is resource-intensive, so choose this option only when
            there are clear analysis needs in Mixpanel.
        </Item>
    </Group>
);

export const DEPRECATED_INFO = (
    <span>Determines whether the event is obsolete and is no longer
        recommended for use in new projects or expanding functionality.
    </span>
);

export const ATTRIBUTES_INFO = (
    <Group direction="v">
        <Item>Attributes contain valuable contextual information attached to a usage event.</Item>
        <Group direction="v" gap="xxs">
            <Item><strong>Follow these basic rules for attributes:</strong></Item>
            <Item>
                • should be human-friendly and understandable.
            </Item>
            <Item>
                • should have more than one possible value in most cases otherwise they are meaningless.
            </Item>
            <Item>
                • should not contain calculated or aggregated dynamic data (should pertain to that specific event only).
            </Item>
            <Item>
                • should not contain sensitive data, the leakage of which could harm the business (such as user data,
                pricing mechanics, and passwords).
            </Item>
            <Item>
                • attributes that catch more than one value should use the "array" data type, and use a plural
                attribute name (e.g. entity_ids instead of entity_id).
            </Item>
            <Item>
                • reuse existing attributes whenever possible (search for an existing attribute with the needed
                Data Type and an attribute name that is relevant).
            </Item>
        </Group>
    </Group>
);

export const ATTRIBUTE_INFO = (
    <Group direction="v" gap="xxs">
        <Item>Name of the attribute.</Item>
        <Item>Use <strong>lowercase</strong> and <strong>underscore</strong> (e.g "account_license_id").</Item>
    </Group>
);

export const ATTRIBUTE_DESCRIPTION_INFO = (
    <span>Succinct description of the attribute.
        Avoid re-writing the attribute name in the description.
    </span>
);

export const DATA_TYPE_INFO = (
    <Group direction="v">
        <Group direction="v" gap="xxs">
            <Item><strong>array</strong></Item>
            <Item>
                Represents a collection of elements. Use an array when multiple values can be stored by the attribute
                at the same time.
            </Item>
            <Item>example: "[Oracle, Meta]", "[Apple]", "[Oracle, Oracle]"</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item><strong>array unique</strong></Item>
            <Item>
                Represents a collection of unique elements. Use an array unique when multiple unique values can
                be stored by the attribute at the same time.
            </Item>
            <Item>example: "[Oracle, Meta]", "[Apple]"</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item><strong>big integer</strong></Item>
            <Item>Stores whole numbers that range from -9,2 quintillion to +9,2 quintillion.</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item><strong>integer</strong></Item>
            <Item>Stores whole numbers that range from -2,1 billion to 2,1 billion.</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item><strong>boolean</strong></Item>
            <Item>Stores two possible values: true or false.</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item><strong>date</strong></Item>
            <Item>Stores date values.</Item>
            <Item>example: "2022-07-01"</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item><strong>date time</strong></Item>
            <Item>Stores date, time, and time zone in one value.</Item>
            <Item>example: "2020-11-05T00:00:00.000Z"</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item><strong>double</strong></Item>
            <Item>Stores decimal values.</Item>
            <Item>example: "1.7241", "100.0"</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item><strong>string</strong></Item>
            <Item>Stores text values.</Item>
            <Item>example: "Oracle", "184736-35"</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item><strong>reference (deprecated)</strong></Item>
            <Item>Stores a JSON object.</Item>
            <Item>example: {'{"segment_id":"60","segment_name":"Information Technology","subsegment":false}'}
            </Item>
        </Group>
    </Group>
);

export const POSSIBLE_VALUES_INFO = (
    <span>Examples of the exact values that the attribute will contain.
        In most cases, <strong>specify at least 2</strong>.
    </span>
);

export const REQUIRED_INFO = (
    <span>Determines if the attribute is required for the event.
        If checked, make sure that the attribute will always be sent with the event,
        otherwise, the event will not be tracked.
    </span>
);

export const ATTRIBUTE_LAST_USAGE_DATE_INFO =
    <span>The date when the attribute was last used in the event.</span>;

export const ATTRIBUTE_DEPRECATED_INFO = (
    <Group direction="v" gap="xxs">
        <Item>Declares that attribute is deprecated for the event for some reason,</Item>
        <Item>for example:</Item>
        <Item>• attribute was declared for the event but never used.</Item>
        <Item>• modifying the functionality where the event triggers.</Item>
    </Group>
);

export const CONTEXTUAL_LINK_INFO = (
    <span>Declares the source for the event, as links to the Jira task,
        Confluence spec, Figma mockups, etc.
    </span>
);

export const CONTEXTUAL_LINK_VALUE_INFO = (
    <Group direction="v">
        <Group direction="v" gap="xxs">
            <Item>Declares a valid link address to the source.</Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item>Examples:</Item>
            <Item>"https://pitchbook.atlassian.net"</Item>
            <Item>"https://www.figma.com"</Item>
        </Group>
    </Group>
);

export const CONTEXTUAL_LINK_TITLE_INFO = (
    <Group direction="v">
        <Group direction="v" gap="xxs">
            <Item>Declares the name of the link source. <strong>Should be between 3 and 30 characters.</strong></Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item>Examples:</Item>
            <Item>"jira"</Item>
            <Item>"confluence"</Item>
            <Item>"figma"</Item>
        </Group>
    </Group>
);

const TRIGGER_ATTACHMENTS_CONFLUENCE_LINK =
    'https://pitchbook.atlassian.net/wiki/spaces/UD/pages/307658871/Event+Tracking+Best+Practices#Trigger-Attachments';

export const TRIGGER_ATTACHMENTS_INFO = (
    <Group direction="v">
        <Group direction="v" gap="xxs">
            <Item>Supported attachment formats are PNG and JPEG images and video.</Item>
            <Item>Maximum size is 25 MB with up to 5 attachments per event.</Item>
            <Item>Use markups to highlight the event location.</Item>
            <Item>
                Avoid videos with excessive context, such as platform login, etc. Include only the necessary context.
            </Item>
        </Group>
        <Group direction="v" gap="xxs">
            <Item>Examples:</Item>
            <Item>
                <a href={TRIGGER_ATTACHMENTS_CONFLUENCE_LINK} target="_blank" rel="noreferrer">
                    Trigger Attachments
                </a>
            </Item>
        </Group>
    </Group>
);
